import React, { useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Heading, SpinningLoader, Text } from '@weave/design-system';
import { MetricAccordion } from '../../../handoff-checklist-v2/components/metric-accordion/metric-accordion';
import { groupMetricsBySection } from '../evaluation-step/unify-evaluation.helpers';
import { UnifyLocationsTable } from '../unify-locations-table/unify-locations-table';
import { useUnifyHandoffContext } from '../../unify-handoff-context';
import { mapSnapshotMetricsToUnifyChecklist } from './unify-snapshot.helpers';

export const UnifyHandoffSnapshot: React.FC = () => {
  const {
    selectedLocations,
    locationDetailsMap,
    snapshotMetricsByLocation,
    isLoadingDraft,
  } = useUnifyHandoffContext();

  const metrics = useMemo(() => {
    return mapSnapshotMetricsToUnifyChecklist(
      snapshotMetricsByLocation,
      selectedLocations
    );
  }, [snapshotMetricsByLocation, selectedLocations]);

  const groupedSections = useMemo(() => {
    return groupMetricsBySection(metrics);
  }, [metrics]);

  if (isLoadingDraft) {
    return (
      <div css={styles.loadingContainer}>
        <SpinningLoader size="large" />
        <Text color="light" size="medium">
          Loading handoff snapshot...
        </Text>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      {/* Location Overview Section */}
      <section css={styles.section}>
        <Heading level={2} css={styles.sectionHeading}>
          Location Overview
        </Heading>
        <UnifyLocationsTable
          locations={selectedLocations}
          mode="snapshot"
          selectedLocationIds={selectedLocations.map((loc) => loc.locationId)}
          locationDetailsMap={locationDetailsMap}
        />
      </section>

      {/* Onboarding Handoff Checklist Section (View-only metrics) */}
      <section css={styles.section}>
        <div css={styles.checklistHeader}>
          <div css={styles.checklistTitleContainer}>
            <Heading level={2} css={styles.sectionHeading}>
              Onboarding Handoff Checklist
            </Heading>
            <Text color="light" size="small" css={styles.description}>
              Ensure all accounts are ready for handoff by viewing the checklists below
            </Text>
          </div>
        </div>

        {groupedSections.length === 0 ? (
          <div css={styles.emptyContainer}>
            <Text color="light" size="medium">
              No checklist metrics available for this handoff.
            </Text>
          </div>
        ) : (
          <div css={styles.sectionsContainer}>
            {groupedSections.map((group) => (
              <div key={group.sectionName} css={styles.sectionGroup}>
                <Heading level={3} css={styles.sectionTitle}>
                  {group.sectionName}
                </Heading>
                <div css={styles.metricList}>
                  {/* View-only checklist metrics with location-wise accordions */}
                  {group.metrics.map((metric) => (
                    <MetricAccordion
                      key={metric.id}
                      metric={metric}
                      defaultOpen={true}
                      readOnly={true}
                      showWhereToFix={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  loadingContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacing(8, 0)};
    gap: ${theme.spacing(2)};
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  sectionHeading: css`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
  `,
  description: css`
    margin: 0;
  `,
  checklistHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  `,
  checklistTitleContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  sectionsContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.5)};
  `,
  sectionGroup: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  sectionTitle: css`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1b2737;
  `,
  metricList: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  emptyContainer: css`
    background-color: #fafbfc;
    border: 1px dashed #d0d4dc;
    border-radius: 6px;
    padding: ${theme.spacing(4)};
    text-align: center;
  `,
};
